import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Kanban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanyLogo } from "@/components/common";
import { EvidenceLabel, INTEL_STAGES, ScorePill, ValueRange, IntelStageChip } from "@/components/intel/primitives";

const START_STAGES = INTEL_STAGES.filter((s) => ["identified", "contacted", "meeting", "qualified"].includes(s.key));

/** Explicit hand-off: creates the intel opportunity and (optionally) the system-of-record Account + Opportunity. */
export function CreateOpportunityDialog({ open, onOpenChange, view, onCreated }) {
  const navigate = useNavigate();
  const [stage, setStage] = useState("identified");
  const [note, setNote] = useState("");
  const [handoff, setHandoff] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setStage("identified");
      setNote("");
      setHandoff(true);
      setResult(null);
    }
  }, [open, view]);

  if (!view) return null;
  const c = view.company || {};
  const existing = view.opportunity;

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const r = await api.post("/intel/opportunities", { company_id: view.company_id, project_id: view.project_id, stage, note });
      let ho = null;
      if (handoff) {
        const h = await api.post(`/intel/opportunities/${r.data.id}/handoff`);
        ho = h.data;
      }
      setResult({ intel: r.data, handoff: ho });
      toast.success("Opportunity created", { description: ho ? "Also created in the Commercial OS pipeline." : "Tracked in the Sales Intelligence pipeline." });
      onCreated?.(r.data, ho);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doHandoff = async () => {
    setBusy(true);
    try {
      const h = await api.post(`/intel/opportunities/${existing.id}/handoff`);
      setResult({ intel: existing, handoff: h.data });
      toast.success("Handed off to Commercial OS");
      onCreated?.(existing, h.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-opportunity-dialog">
        {result ? (
          <div className="py-2" data-testid="create-opportunity-success">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--status-ok)/0.10)] text-[hsl(var(--status-ok))]"><CheckCircle2 className="h-6 w-6" /></div>
            <DialogHeader>
              <DialogTitle>Opportunity created</DialogTitle>
              <DialogDescription>{result.intel.name}</DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-2 text-[13px]">
              <div className="flex items-center justify-between rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3.5 py-2.5">
                <span className="text-muted-foreground">Sales Intelligence pipeline</span>
                <IntelStageChip stage={result.handoff ? "qualified" : result.intel.stage} />
              </div>
              {result.handoff ? (
                <div className="flex items-center justify-between rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3.5 py-2.5">
                  <span className="text-muted-foreground">Commercial OS pipeline</span>
                  <span className="inline-flex items-center gap-1.5 font-medium"><Kanban className="h-3.5 w-3.5 text-primary" /> Discovery stage</span>
                </div>
              ) : null}
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-opportunity-done-button">Stay here</Button>
              <Button variant="soft" onClick={() => { onOpenChange(false); navigate("/intel/opportunities"); }} data-testid="create-opportunity-open-intel-pipeline-button">Intel pipeline</Button>
              {result.handoff ? (
                <Button onClick={() => { onOpenChange(false); navigate(`/opportunities/${result.handoff.opportunity_id}`); }} data-testid="create-opportunity-open-os-button">Open opportunity <ArrowUpRight className="h-4 w-4" /></Button>
              ) : null}
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>{existing ? "Hand off to Commercial OS" : "Create opportunity"}</DialogTitle>
              <DialogDescription>{existing ? "This project is already tracked in the intel pipeline. Create the system-of-record opportunity now." : "Nothing is written to the CRM until you confirm. You can keep it in the intel pipeline only."}</DialogDescription>
            </DialogHeader>

            <div className="mt-4 rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)] p-3.5" data-testid="create-opportunity-summary">
              <div className="flex items-start gap-3">
                <CompanyLogo name={c.name} domain={c.website} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-semibold">{c.name}</div>
                  <div className="truncate text-[12.5px] text-muted-foreground">{view.project_name} · {view.project_type_label}</div>
                </div>
                <ScorePill score={view.score} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <div className="eyebrow text-[10px]">Estimated range</div>
                  <div className="mt-0.5 flex items-center gap-1.5"><ValueRange label={view.value_label} /><EvidenceLabel kind="hypothesis" compact /></div>
                </div>
                <div>
                  <div className="eyebrow text-[10px]">Likely scope</div>
                  <div className="mt-0.5 truncate">{(view.solutions || []).slice(0, 3).map((s) => s.category).join(", ") || "Unknown"}</div>
                </div>
              </div>
            </div>

            {!existing ? (
              <div className="mt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label>Starting stage</Label>
                  <Select value={stage} onValueChange={setStage}>
                    <SelectTrigger className="h-10 rounded-[10px]" data-testid="create-opportunity-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{START_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="io-note">Note (optional)</Label>
                  <Textarea id="io-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why we are pursuing this, who we spoke to…" className="rounded-[10px]" data-testid="create-opportunity-note-input" />
                </div>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-black/[0.07] bg-white px-3.5 py-3" data-testid="create-opportunity-handoff-toggle">
                  <Checkbox checked={handoff} onCheckedChange={(v) => setHandoff(!!v)} className="mt-0.5" data-testid="create-opportunity-handoff-checkbox" />
                  <span className="text-[13px] leading-snug">
                    <span className="font-medium">Also create in the Commercial OS pipeline</span>
                    <span className="block text-[12.5px] text-muted-foreground">Creates (or links) the account and opens a Discovery-stage opportunity you can take into the AI Workspace.</span>
                  </span>
                </label>
              </div>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              {existing ? (
                <Button type="button" onClick={doHandoff} disabled={busy} data-testid="create-opportunity-handoff-button">{busy ? "Working…" : "Create in Commercial OS"}</Button>
              ) : (
                <Button type="submit" disabled={busy} data-testid="create-opportunity-submit-button">{busy ? "Creating…" : handoff ? "Create opportunity" : "Track in intel pipeline"}</Button>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
